/**
 * Schedule an interview against an application — `POST
 * /recruiting/applications/{id}/interviews`, WP-W4.
 *
 * An interview hangs off an application, so an application is the one required choice
 * and the submit stays disabled until it is made — a true sentence about the operator
 * ("you have not picked an application"), not about permission. Everything else — the
 * kind, the two times, the timezone, the location, the meeting URL, and the initial
 * panel — is optional, and a blank is **omitted** rather than sent empty, because
 * `exactOptionalPropertyTypes` makes those two different requests.
 *
 * ── Pickers, or a plain id when the list is out of reach ─────────────────────
 *
 * The application list reads on `recruiting.view_candidates`; a session that reaches
 * this screen on `recruiting.submit_feedback` alone cannot read it, so when the options
 * are `null` the application becomes a plain id input — the same honest fallback the
 * pipeline create form uses. The panelists are employee ids too: `GET /employees`
 * (Task T2-A) and `EmployeePicker` (Task T2-B) exist now, but this form has not been
 * rewired to use them, so each is still a plain id input with a role.
 *
 * ── Times are captured as instants ──────────────────────────────────────────
 *
 * The two schedule fields are `datetime-local` — wall-clock, no zone — and are sent as
 * ISO instants (`new Date(value).toISOString()`). The separate `timezone` field records
 * the zone the interview is *meant* to be read in, which is what the server stores
 * alongside the instant; it defaults to the session's zone.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  InterviewKind,
  PanelRole,
  RecruitingInterview,
  RecruitingInterviewScheduleRequest,
  RecruitingPanelistInput,
} from '../../api/recruiting';
import { INTERVIEW_KINDS, PANEL_ROLES } from '../../api/recruiting';
import type { WorkforceApiError } from '../../api/errors';
import { INTERVIEW_KIND_LABEL, PANEL_ROLE_LABEL } from '../../recruiting/interview';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';
import type { PickerOption } from './CreateApplicationForm';

interface PanelistRow {
  readonly employeeId: string;
  readonly role: PanelRole;
}

export interface ScheduleInterviewFormProps {
  /** `null` when the applications list is out of reach — the field falls back to an id input. */
  readonly applicationOptions: readonly PickerOption[] | null;
  readonly defaultTimezone: string;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly created: RecruitingInterview | null;
  readonly onSubmit: (applicationId: string, body: RecruitingInterviewScheduleRequest) => void;
}

/** A `datetime-local` value → an ISO instant, or `undefined` when blank/unparseable. */
function toInstant(value: string): string | undefined {
  if (value.trim() === '') return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export function ScheduleInterviewForm({
  applicationOptions,
  defaultTimezone,
  pending,
  error,
  created,
  onSubmit,
}: ScheduleInterviewFormProps): ReactNode {
  const { t } = useI18n();
  const [applicationId, setApplicationId] = useState('');
  const [kind, setKind] = useState<'' | InterviewKind>('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [location, setLocation] = useState('');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [panelists, setPanelists] = useState<readonly PanelistRow[]>([]);

  const ready = applicationId.trim().length > 0;

  const build = (): RecruitingInterviewScheduleRequest => {
    const seats: RecruitingPanelistInput[] = panelists
      .filter((row) => row.employeeId.trim() !== '')
      .map((row) => ({ interviewer_employee_id: row.employeeId.trim(), panel_role: row.role }));
    const startIso = toInstant(start);
    const endIso = toInstant(end);
    const tz = timezone.trim();
    const loc = location.trim();
    const url = meetingUrl.trim();
    return {
      ...(kind === '' ? {} : { kind }),
      ...(startIso === undefined ? {} : { scheduled_start: startIso }),
      ...(endIso === undefined ? {} : { scheduled_end: endIso }),
      ...(tz === '' ? {} : { timezone: tz }),
      ...(loc === '' ? {} : { location: loc }),
      ...(url === '' ? {} : { meeting_url: url }),
      ...(seats.length === 0 ? {} : { panelists: seats }),
    };
  };

  return (
    <section className="wp-section" aria-labelledby="w4-schedule-heading" data-testid="w4-schedule">
      <h2 className="wp-section__title" id="w4-schedule-heading">
        {t('recruiting.interviews.schedule.title')}
      </h2>
      <p className="wp-small wp-muted">{t('recruiting.interviews.schedule.intro')}</p>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || pending) return;
          onSubmit(applicationId.trim(), build());
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-schedule-application">
            {t('recruiting.interviews.schedule.application')}
          </label>
          {applicationOptions === null ? (
            <>
              <input
                className="wp-field__input"
                id="w4-schedule-application"
                data-testid="w4-schedule-application"
                type="text"
                value={applicationId}
                autoComplete="off"
                onChange={(event) => {
                  setApplicationId(event.target.value);
                }}
              />
              <p className="wp-small wp-muted">
                {t('recruiting.interviews.schedule.applicationIdHint')}
              </p>
            </>
          ) : (
            <select
              className="wp-field__input"
              id="w4-schedule-application"
              data-testid="w4-schedule-application"
              value={applicationId}
              onChange={(event) => {
                setApplicationId(event.target.value);
              }}
            >
              <option value="">{t('recruiting.interviews.schedule.applicationPlaceholder')}</option>
              {applicationOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-schedule-kind">
            {t('recruiting.interviews.schedule.kind')}
          </label>
          <select
            className="wp-field__input"
            id="w4-schedule-kind"
            data-testid="w4-schedule-kind"
            value={kind}
            onChange={(event) => {
              const next = event.target.value;
              setKind(next === '' ? '' : (next as InterviewKind));
            }}
          >
            <option value="">{t('recruiting.interviews.schedule.kindPlaceholder')}</option>
            {INTERVIEW_KINDS.map((value) => (
              <option key={value} value={value}>
                {t(INTERVIEW_KIND_LABEL[value])}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-schedule-start">
            {t('recruiting.interviews.schedule.start')}
          </label>
          <input
            className="wp-field__input"
            id="w4-schedule-start"
            data-testid="w4-schedule-start"
            type="datetime-local"
            value={start}
            onChange={(event) => {
              setStart(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-schedule-end">
            {t('recruiting.interviews.schedule.end')}
          </label>
          <input
            className="wp-field__input"
            id="w4-schedule-end"
            data-testid="w4-schedule-end"
            type="datetime-local"
            value={end}
            onChange={(event) => {
              setEnd(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-schedule-timezone">
            {t('recruiting.interviews.schedule.timezone')}
          </label>
          <input
            className="wp-field__input"
            id="w4-schedule-timezone"
            data-testid="w4-schedule-timezone"
            type="text"
            value={timezone}
            autoComplete="off"
            onChange={(event) => {
              setTimezone(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-schedule-location">
            {t('recruiting.interviews.schedule.location')}
          </label>
          <input
            className="wp-field__input"
            id="w4-schedule-location"
            data-testid="w4-schedule-location"
            type="text"
            value={location}
            autoComplete="off"
            onChange={(event) => {
              setLocation(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-schedule-meeting-url">
            {t('recruiting.interviews.schedule.meetingUrl')}
          </label>
          <input
            className="wp-field__input"
            id="w4-schedule-meeting-url"
            data-testid="w4-schedule-meeting-url"
            type="url"
            value={meetingUrl}
            autoComplete="off"
            onChange={(event) => {
              setMeetingUrl(event.target.value);
            }}
          />
        </div>

        <fieldset className="wp-filters__group" data-testid="w4-schedule-panel">
          <legend className="wp-filters__legend">
            {t('recruiting.interviews.schedule.panel')}
          </legend>
          <p className="wp-small wp-muted">{t('recruiting.interviews.schedule.panelHint')}</p>
          {panelists.map((row, index) => (
            <div
              className="wp-filters__group"
              key={index}
              data-testid={`w4-schedule-panelist-${index}`}
            >
              <input
                className="wp-field__input"
                aria-label={t('recruiting.interviews.schedule.panelistId')}
                data-testid={`w4-schedule-panelist-id-${index}`}
                type="text"
                value={row.employeeId}
                autoComplete="off"
                placeholder={t('recruiting.interviews.schedule.panelistId')}
                onChange={(event) => {
                  const value = event.target.value;
                  setPanelists((rows) =>
                    rows.map((existing, i) =>
                      i === index ? { ...existing, employeeId: value } : existing,
                    ),
                  );
                }}
              />
              <select
                className="wp-field__input"
                aria-label={t('recruiting.interviews.schedule.panelistRole')}
                data-testid={`w4-schedule-panelist-role-${index}`}
                value={row.role}
                onChange={(event) => {
                  const value = event.target.value as PanelRole;
                  setPanelists((rows) =>
                    rows.map((existing, i) =>
                      i === index ? { ...existing, role: value } : existing,
                    ),
                  );
                }}
              >
                {PANEL_ROLES.map((value) => (
                  <option key={value} value={value}>
                    {t(PANEL_ROLE_LABEL[value])}
                  </option>
                ))}
              </select>
              <button
                className="wp-button"
                type="button"
                data-testid={`w4-schedule-panelist-remove-${index}`}
                onClick={() => {
                  setPanelists((rows) => rows.filter((_, i) => i !== index));
                }}
              >
                {t('recruiting.interviews.schedule.panelistRemove')}
              </button>
            </div>
          ))}
          <button
            className="wp-button"
            type="button"
            data-testid="w4-schedule-panelist-add"
            onClick={() => {
              setPanelists((rows) => [...rows, { employeeId: '', role: 'panelist' }]);
            }}
          >
            {t('recruiting.interviews.schedule.panelistAdd')}
          </button>
        </fieldset>

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w4-schedule-submit"
            disabled={!ready || pending}
          >
            {pending
              ? t('recruiting.interviews.schedule.submitting')
              : t('recruiting.interviews.schedule.submit')}
          </button>
        </div>
      </form>

      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('recruiting.interviews.subjectWord')}
          testId="w4-schedule-error"
        />
      )}
      {created === null ? null : (
        <StatusNote
          severity="low"
          title={t('recruiting.interviews.schedule.ok')}
          testId="w4-schedule-ok"
        />
      )}
    </section>
  );
}
